import { SummaryTableData } from '@/services/whatIfAnalysis.service'
import { themeClassName } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ColDef } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import {
    Box,
    Typography
} from '@mui/material'
import React, { useMemo } from 'react'

interface SummaryCharProps {
  loading?: boolean
  data?: SummaryTableData[]
  error?: string | null
}

const SummaryChar: React.FC<SummaryCharProps> = ({ loading = false, data, error }) => {
  // Default mock data - used when no API data is provided
  const SummaryData: SummaryTableData[] = [
    {
      metric: 'Market Value ($ Millions)',
      dbField: 'Mkval',
      before: '2,603.64',
      after: '2,603.64',
      change: '0.00',
      benchmark: '28,325.62'
    },
    {
      metric: 'Level Effective Duration',
      dbField: 'EDur',
      before: '12.13',
      after: '13.19',
      change: '1.07',
      benchmark: '6.23'
    },
    {
      metric: 'Level Par Effective Duration',
      dbField: 'PDur',
      before: '12.31',
      after: '13.38',
      change: '1.07',
      benchmark: '6.30'
    },
    {
      metric: 'Slope Effective Duration',
      dbField: 'ED2',
      before: '10.54',
      after: '11.30',
      change: '0.75',
      benchmark: '4.45'
    },
    {
      metric: 'Curvature Effective Duration',
      dbField: 'ED3',
      before: '8.25',
      after: '9.12',
      change: '0.87',
      benchmark: '3.78'
    },
    {
      metric: 'Convexity',
      dbField: 'Cvx',
      before: '0.18',
      after: '0.21',
      change: '0.03',
      benchmark: '0.15'
    },
    {
      metric: 'Yield to Effective',
      dbField: 'YTE',
      before: '3.45',
      after: '3.52',
      change: '0.07',
      benchmark: '3.28'
    },
    {
      metric: 'Quality',
      dbField: 'Qual',
      before: '2.15',
      after: '2.08',
      change: '-0.07',
      benchmark: '2.35'
    },
    {
      metric: 'Coupon',
      dbField: 'Coupon',
      before: '4.25',
      after: '4.18',
      change: '-0.07',
      benchmark: '4.12'
    },
    {
      metric: 'Years to Maturity',
      dbField: 'Yrs to Mat',
      before: '8.75',
      after: '9.12',
      change: '0.37',
      benchmark: '7.85'
    },
    {
      metric: 'Average Life',
      dbField: 'AvgLif',
      before: '6.85',
      after: '7.12',
      change: '0.27',
      benchmark: '6.45'
    },
    {
      metric: 'Risk',
      dbField: 'Risk',
      before: '1.25',
      after: '1.18',
      change: '-0.07',
      benchmark: '1.35'
    },
    {
      metric: 'Tracking Error',
      dbField: 'T/E',
      before: '0.85',
      after: '0.92',
      change: '0.07',
      benchmark: '0.75'
    },
    {
      metric: '1 Day 95% Value at Risk (000)',
      dbField: 'Var95D',
      before: '125.5',
      after: '132.8',
      change: '7.3',
      benchmark: '118.2'
    }
  ]

  // Process data to handle missing values and zero changes at data layer
  const summaryData = useMemo(() => {
    const rawData = data || SummaryData
    return rawData.map(item => ({
      ...item,
      before: item.before || '---',
      after: item.after || '---',
      change: (!item.change || 
               item.change === 'No Change' || 
               item.change === '0' || 
               item.change === '0.00' ||
               parseFloat(item.change) === 0) ? '---' : item.change,
      benchmark: item.benchmark || '---'
    }))
  }, [data])

  // Column definitions matching the table structure
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'Metric',
      field: 'metric',
      flex: 2.5,
      minWidth: 250,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'startsWith', 'endsWith'],
        defaultOption: 'contains'
      },
      headerClass: 'ag-header-cell-left',
      tooltipField: 'dbField',
      tooltipComponentParams: {
        color: '#1976d2'
      }
    },
    {
      headerName: 'Before',
      field: 'before',
      flex: 1,
      minWidth: 120,
      filter: 'agNumberColumnFilter',
      headerClass: 'ag-header-cell-center',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'After',
      field: 'after',
      flex: 1,
      minWidth: 120,
      filter: 'agNumberColumnFilter',
      headerClass: 'ag-header-cell-center',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'Change',
      field: 'change',
      flex: 1,
      minWidth: 100,
      filter: 'agNumberColumnFilter',
      headerClass: 'ag-header-cell-center',
      cellStyle: (params: any) => {
        // Handle pre-processed '---' values for no change
        if (params.value === '---') {
          return {
            textAlign: 'right',
            color: '#000',
            fontWeight: 'normal',
          }
        }

        const changeValue = parseFloat(params.value)
        let color = '#000'

        // Match What-If grid colors: #4CAF50 for positive, #F44336 for negative
        if (!Number.isNaN(changeValue)) {
          if (changeValue > 0) {
            color = '#4CAF50'
          } else if (changeValue < 0) {
            color = '#F44336'
          }
        }

        return {
          textAlign: 'right',
          color,
          fontWeight: 'bold',
        }
      },
    },
    {
      headerName: 'Benchmark',
      field: 'benchmark',
      flex: 1,
      minWidth: 120,
      filter: 'agNumberColumnFilter',
      headerClass: 'ag-header-cell-center',
      cellStyle: { textAlign: 'right' }
    }
  ], [])

  // Default column properties
  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    suppressMenu: false,
    floatingFilter: false
  }), [])

  // Grid options
  const gridOptions = useMemo(() => ({
    suppressRowClickSelection: true,
    suppressCellFocus: true,
    enableCellTextSelection: true,
    animateRows: true,
    suppressRowHoverHighlight: false,
    suppressMenuHide: false,
    enableBrowserTooltips: true,
    tooltipShowDelay: 500,
    suppressLoadingOverlay: false,
    suppressNoRowsOverlay: false,
    suppressContextMenu: true
  }), [])

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #e0e0e0',
      borderRadius: 1,
      backgroundColor: 'white'
    }}>
      {/* Header */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        p: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#000000' }}>
          Summary Characteristics
        </Typography>
      </Box>

      {/* AG-Grid */}
      <Box sx={{ flex: 1, width: '100%' }} className={themeClassName}>
        <AgGridReact
          rowData={summaryData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          modules={[ClientSideRowModelModule]}
          loading={loading}
          loadingOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography>Loading summary data...</Typography>
            </Box>
          )}
          noRowsOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="textSecondary">
                {error || 'No summary data available'}
              </Typography>
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}

export default SummaryChar